import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import siteContent from '../../content/siteContent'
import './Home.css'

gsap.registerPlugin(ScrollTrigger)

export default function Home() {
  const navigate = useNavigate()
  const content = siteContent.home

  useEffect(() => {
    const pinnedSection = document.querySelector('.pinned-tabs-section')
    const tabButtons = document.querySelectorAll('.tab-nav-btn')
    const contentDisplays = document.querySelectorAll('.content-display-item') // Right side
    const tabContentItems = document.querySelectorAll('.tab-content-item') // Left side

    if (pinnedSection) {
      let currentTab = 0

      const updateTab = (tabIndex) => {
        // Update Buttons
        tabButtons.forEach((btn, idx) => {
          btn.classList.toggle('active', idx === tabIndex)
        })

        // Update Right side items
        contentDisplays.forEach((display, idx) => {
          const isActive = idx === tabIndex
          display.classList.toggle('active', isActive)
          gsap.to(display, { opacity: isActive ? 1 : 0, duration: 0.4 })
        })

        // Update Left side items (New logic)
        tabContentItems.forEach((item, idx) => {
          const isActive = idx === tabIndex
          item.classList.toggle('active', isActive)
          gsap.to(item, { opacity: isActive ? 1 : 0, duration: 0.4 })
        })

        currentTab = tabIndex
      }

      // GSAP ScrollTrigger
      ScrollTrigger.create({
        trigger: pinnedSection,
        start: 'top top',
        end: '+=2000',
        pin: true,
        pinSpacing: true,
        scrub: 1,
        onUpdate: (self) => {
          const progress = self.progress
          const tabIndex = Math.floor(progress * 3.99) // Maps 0-1 to 0-3

          if (tabIndex !== currentTab) {
            updateTab(tabIndex)
          }
        }
      })

      // Handle tab button clicks: Scroll the page to the specific progress point
      tabButtons.forEach((btn, idx) => {
        btn.addEventListener('click', () => {
          // Calculate the scroll position based on the index
          const start = ScrollTrigger.create({ trigger: pinnedSection, start: 'top top' }).start
          const scrollPos = start + (idx / 3) * 2000

          window.scrollTo({
            top: scrollPos,
            behavior: 'smooth'
          })
        })
      })

      updateTab(0)
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill())
    }
  }, [])

  const handleButtonClick = (buttonText) => {
    if (buttonText.toLowerCase().includes('demo') || buttonText.toLowerCase().includes('schedule') || buttonText.toLowerCase().includes('get started')) {
      navigate('/contact')
    }
  }

  const getDeviceImage = (title) => {
    if (title.toLowerCase().includes('mobile')) {
      return '/images/mobile.png'
    } else if (title.toLowerCase().includes('web') || title.toLowerCase().includes('extension')) {
      return '/images/extension.png'
    }
    return null
  }

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <h1>
            {content.hero.headline}
            <br />
            <span className="highlight">{content.hero.highlightedPhrase}</span>
          </h1>
          <p className="hero-subheading">{content.hero.subheading}</p>
          <div className="hero-buttons">
            {content.hero.buttons.map((btn, idx) => (
              <button
                key={idx}
                className={`btn-${btn.variant}`}
                onClick={() => handleButtonClick(btn.text)}
              >
                {btn.text}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Care Partner Section */}
      <section className="care-partner-section">
        <div className="container">
          <div className="care-partner-grid">
            <div className="care-partner-image">
              <div className="home-placeholder-large">
                <img
                  src="/images/homebanner.png"
                  alt="Home Banner"
                  className="home-placeholder-large"
                  style={{ objectFit: 'cover' }}
                />
              </div>
            </div>
            <div className="care-partner-text">
              <h2>{content.carePartner.heading}</h2>
              <p>{content.carePartner.description}</p>
              <ul className="care-partner-bullets">
                {content.carePartner.bulletPoints.map((bullet, idx) => (
                  <li key={idx}>
                    <span className="bullet-icon">✓</span>
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pinned Scroll Tabs Section - NEW COMPONENT */}
      <section className="pinned-tabs-section">
        <div className="sticky-tabs-container">
          {/* Left Panel */}
          <div className="tabs-left-panel">
            <div className="tabs-content-wrapper">
              <div className="tab-content-item" data-tab="0">
                <h2>Launch a new consultation session in seconds</h2>
                <p>Pull patient context including patient summary and insights from EMR records before or during the encounter.</p>
                <ul>
                  <li>One-click session launch</li>
                  <li> EMR patient history pull</li>
                  <li>Patient summary insights</li>
                  <li>Pre-encounter context loading</li>
                </ul>
              </div>

              <div className="tab-content-item" data-tab="1">
                <h2>Keep evidence where it matters</h2>
                <p>Select from specialty-specific clinical templates or create a custom template tailored to your workflow and EMR requirements.</p>
                <ul>
                  <li>Preset clinical templates</li>
                  <li>Specialty-matched formats</li>
                  <li>Custom template builder</li>
                  <li>One-click template switching</li>
                </ul>
              </div>

              <div className="tab-content-item" data-tab="2">
                <h2>AI-powered clinical documentation</h2>
                <p>Capture the patient-practitioner conversation in real-time with medical-grade accuracy. Utilio listens while you focus on the patient.</p>
                <ul>
                  <li>Real-time AI transcription</li>
                  <li>Medical terminology recognition</li>
                  <li>Multi-speaker detection</li>
                  <li>Background noise filtering</li>
                </ul>
              </div>

              <div className="tab-content-item" data-tab="3">
                <h2>Close every loop after the visit</h2>
                <p>Our intelligent scribe learns your style and adapts to your workflow. Auto-completion and smart templates save you time on documentation.</p>
                <ul>
                  <li>Intelligent notes structuring</li>
                  <li>Magic paste feature to auto-populated EMR fields</li>
                </ul>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="tabs-nav">
              <button className="tab-nav-btn active" data-tab="0">Session</button>
              <button className="tab-nav-btn" data-tab="1">Template</button>
              <button className="tab-nav-btn" data-tab="2">Transcribe</button>
              <button className="tab-nav-btn" data-tab="3">Notes</button>
            </div>
          </div>

          {/* Right Panel */}
          <div className="tabs-right-panel">
            {/* Session */}
            <div className="content-display-item active" data-content="0">
              <div className="mockup-container gif-display-container">
                <img
                  src="/images/newsession.gif"
                  alt="Mobile Session Launch"
                  className="centered-gif"
                />
                <div className="mobile-only-label">
                  Quick Session Launch
                </div>
              </div>
            </div>

            {/* Template */}
            <div className="content-display-item" data-content="1">
              <div className="mockup-container gif-display-container">
                <img
                  src="/images/template.gif"
                  alt="Template Selection"
                  className="centered-gif"
                />
                <div className="mobile-only-label">
                  Customized Template Selection
                </div>
              </div>
            </div>

            {/* Transcribe */}
            <div className="content-display-item" data-content="2">
              <div className="mockup-container gif-display-container">
                <img
                  src="/images/record.gif"
                  alt="Transcription"
                  className="centered-gif"
                />
                <div className="mobile-only-label">
                  Real-time AI transcription
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="content-display-item" data-content="3">
              <div className="mockup-container gif-display-container">
                <img
                  src="/images/notes.gif"
                  alt="Notes"
                  className="centered-gif"
                />
                <div className="mobile-only-label">
                  Real-time AI transcription
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Device Section */}
      <section className="device-section">
        <div className="container">
          <div className="device-header">
            <h2>{content.deviceSection.heading}</h2>
            <p>{content.deviceSection.subheading}</p>
          </div>
          <div className="device-grid">
            {content.deviceSection.devices.map((device, idx) => {
              const imagePath = getDeviceImage(device.title)
              return (
                <div key={idx} className="device-card">
                  {imagePath ? (
                    <img src={imagePath} alt={device.title} className="device-image" />
                  ) : (
                    <div className="device-icon">{device.icon}</div>
                  )}
                  <h3>{device.title}</h3>
                  <p>{device.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="impact-section">
        <div className="container">
          <h2 className="impact-title">{content.impact.heading}</h2>
          <div className="stats-grid">
            {content.impact.stats.map((stat, idx) => (
              <div
                key={idx}
                className={`stat-card ${stat.highlighted ? 'highlighted' : ''}`}
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>

                {/* External Link Buttons */}
                <div className="stat-cta-wrapper">
                  <a
                    href={stat.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`stat-link ${idx === 0 ? 'link-underlined' : 'stat-btn'}`}
                  >
                    {stat.linkText}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}